import { css } from '@linaria/atomic';
import { breakpoints } from '@frontend/responsiveness';
import { theme, GRID_UNIT } from '@frontend/theme';

const STEPPER_ICON_WIDTH = 44;

export type StepperColorProps = {
  hasPrev: boolean;
  isNext: boolean;
  active: boolean;
  complete: boolean;
  valid: boolean;
  index: number;
  percentComplete: number;
  maxIndex: number;
  isClickable: boolean;
  clickDisabled: boolean | undefined;
};

export function getStepperColors({ hasPrev, isNext, active, valid }: StepperColorProps) {
  if (hasPrev) {
    return {
      iconBorder: theme.colors.primary50,
      iconBackground: theme.colors.white,
      iconColor: theme.colors.primary50,
      color: theme.colors.neutral90,
    };
  }

  if (isNext && valid) {
    return {
      iconBorder: theme.colors.neutral40,
      iconBackground: theme.colors.white,
      iconColor: theme.colors.neutral40,
      color: theme.colors.neutral40,
    };
  }
  if (active) {
    return {
      iconBorder: theme.colors.primary50,
      iconBackground: theme.colors.primary50,
      iconColor: theme.colors.white,
      color: theme.colors.neutral90,
    };
  }

  // disabled is left
  return {
    iconBorder: theme.colors.neutral40,
    iconBackground: theme.colors.white,
    iconColor: theme.colors.neutral40,
    color: theme.colors.neutral20,
  };
}

export const rootContainer = css`
  flex: 1;
`;

export const rootContainerResponsive = css`
  height: 100%;
`;

export const root = css`
  align-items: stretch;
  min-height: 500px;
  max-height: 100%;
  background-color: ${theme.colors.white};
  display: flex;
`;

export const rootInModal = css`
  padding: ${theme.spacing(1)} ${theme.spacing(4)};
`;

export const rootNotInModal = css`
  padding: ${theme.spacing(4)};
  box-shadow: 0px 8px 12px 6px rgba(0, 33, 82, 0.15), 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
`;

export const rootRounded = css`
  border-radius: ${theme.borderRadius.medium};
`;

export const stepperWrapper = css`
  border-right: solid 1px ${theme.colors.neutral20};
  flex: 0 0 auto;
  max-height: 100%;
  max-width: 167px;
`;

export const stepperWrapperFull = css`
  margin-left: 0;
  width: 40%;
`;

export const stepperWrapperCompact = css`
  margin-left: ${-(GRID_UNIT * 2)}px;
  width: 54px;
`;

export const body = css`
  flex-grow: 1;
`;

export const bodyPaddingNone = css`
  padding-left: 0px;
`;

export const bodyPaddingFull = css`
  padding-left: ${theme.spacing(4)};
`;

export const bodyPaddingCompact = css`
  padding-left: ${theme.spacing(2)};
`;

export const stepper = css`
  margin-right: ${theme.spacing(4)};
`;

export const stepperItem = css`
  color: var(--stepper-item-color);
  font-weight: 400;
  height: 64px;
  position: relative;
`;

export const stepperItemActive = css`
  font-weight: 700;
`;

export const stepperItemClickable = css`
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.neutral10};
  }
`;

export const stepperItemNotClickable = css`
  cursor: not-allowed;
`;

export const stepperIcon = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: var(--stepper-icon-size);
  width: var(--stepper-icon-size);
  border: solid 2px var(--stepper-icon-border);
  background-color: var(--stepper-icon-bg);
  color: var(--stepper-icon-color);
  border-radius: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 15;
  transition: all 0.3s ease;
`;

export const stepperIconWrapper = css`
  width: ${STEPPER_ICON_WIDTH}px;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
`;

export const stepperLabel = css`
  padding-left: ${STEPPER_ICON_WIDTH + 2}px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  user-select: none;
`;

export const stepperLabelText = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
`;

export const stepProgress = css`
  position: absolute;
  width: 2px;
  height: var(--step-progress-height);
  left: 50%;
  transform: translateX(-50%);
  top: calc(50% + var(--step-progress-offset));
  background: var(--step-progress-bg);
  transition: height 0.3s ease, background 0.3s ease;
  z-index: 15;
`;

export const step = css`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  row-gap: ${theme.spacing(2)};
`;

export const stepHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.neutral90};
  flex-shrink: 1;
  column-gap: ${theme.spacing(2)};
`;

export const stepBody = css`
  flex-grow: 1;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: visible;
`;

export const stepFooter = css`
  flex-shrink: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  column-gap: ${theme.spacing(2)};
  row-gap: ${theme.spacing(2)};

  @media screen and (max-width: ${breakpoints.small.max}px) {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
`;

export const stepBackButton = css`
  margin-right: auto;
`;

export const helpText = css`
  color: ${theme.colors.neutral50};
`;
